import React from 'react';
import ActionsMenu from './ActionsMenu';

/**
 * The Actions menu for a row in a documents table.
 *
 * Every documents table in the admin offers roughly the same handful of things -- view, download,
 * sometimes remove -- and they were each drawn as loose icon buttons, so the same action sat in a
 * different place and wore a different shape on every screen. Reuse the shared Actions menu,
 * rendered outside the table so scrolling containers cannot clip the document actions.
 */

export interface DocumentAction {
  label: string;
  /** Font Awesome class, e.g. "fa-eye". */
  icon: string;
  onClick: () => void;
  /** Destructive actions are tinted and sit below a divider. */
  destructive?: boolean;
  /** Shows a spinner and blocks the item while the action is running. */
  busy?: boolean;
  disabled?: boolean;
}

interface DocumentActionsMenuProps {
  /** Unique within the table; used for the toggle's id. */
  id: string;
  actions: DocumentAction[];
}

const DocumentActionsMenu: React.FC<DocumentActionsMenuProps> = ({ id, actions }) => {
  const available = actions.filter(Boolean);
  if (available.length === 0) return null;

  const destructive = available.filter(action => action.destructive);
  const ordinary = available.filter(action => !action.destructive);

  return (
    <ActionsMenu id={`document-actions-${id}`} items={[...ordinary, ...destructive].map((action, index) => ({
      label: action.busy ? 'Working...' : action.label,
      icon: action.busy ? 'fa-spinner fa-spin' : action.icon,
      disabled: action.disabled || action.busy,
      onClick: action.onClick,
      variant: action.destructive ? 'danger' : 'default',
      dividerBefore: action.destructive && ordinary.length > 0 && index === ordinary.length
    }))} />
  );
};

export default DocumentActionsMenu;
